import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { APP_ROUTES } from "../../constants/approutes";
import FullScreenLoader from "../FullScreenLoader";
import { Button } from "antd";
import { CameraOutlined } from "@ant-design/icons";

const API_URL = process.env.REACT_APP_API_URL;

const VITAL_THRESHOLDS = {
  temperature: { min: 95, max: 99.5, criticalMin: 90, criticalMax: 105 },
  pulse: { min: 60, max: 100, criticalMin: 40, criticalMax: 140 },
  heartRate: { min: 60, max: 100, criticalMin: 40, criticalMax: 140 },
  respirationRate: { min: 12, max: 20, criticalMin: 8, criticalMax: 30 },
  bloodGlucose: { min: 70, max: 140, criticalMin: 50, criticalMax: 250 },
  bloodPressureSystolic: {
    min: 90,
    max: 120,
    criticalMin: 70,
    criticalMax: 180,
  },
  bloodPressureDiastolic: {
    min: 60,
    max: 80,
    criticalMin: 40,
    criticalMax: 120,
  },
  weight: { min: 10, max: 200 },
  oxygenLevel: { min: 95, max: 100, criticalMin: 90 },
};

const getVitalStatus = (value, thresholds) => {
  if (value === "") return "empty";
  const num = parseFloat(value);
  if (isNaN(num)) return "invalid";

  if (thresholds.criticalMin !== undefined && num < thresholds.criticalMin)
    return "critical";
  if (thresholds.criticalMax !== undefined && num > thresholds.criticalMax)
    return "critical";
  if (num < thresholds.min || num > thresholds.max) return "abnormal";

  return "normal";
};

function HeightInput({ height, onChange }) {
  const handleUnitSwitch = (e) => {
    onChange({ ...height, unit: e.target.value });
  };
  const handleField = (field) => (e) => {
    onChange({ ...height, [field]: e.target.value });
  };

  return (
    <div className="flex flex-col">
      <label className="text-base text-[#004276] mb-1 font-semibold">Height</label>
      <div className="flex items-center gap-2">
        <select
          value={height.unit}
          onChange={handleUnitSwitch}
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition"
        >
          <option value="cm">cm</option>
          <option value="ft">ft/in</option>
        </select>

        {height.unit === "cm" ? (
          <div className="relative flex-1">
            <input
              type="number"
              value={height.cm}
              onChange={handleField("cm")}
              className="w-full rounded-lg border border-gray-200 px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#004276]">
              cm
            </span>
          </div>
        ) : (
          <div className="flex gap-2 flex-1">
            <div className="relative flex-1">
              <input
                type="number"
                value={height.ft}
                onChange={handleField("ft")}
                placeholder="Feet"
                className="w-full rounded-lg border border-gray-200 px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#004276]">
                ft
              </span>
            </div>
            <div className="relative flex-1">
              <input
                type="number"
                value={height.inch}
                onChange={handleField("inch")}
                placeholder="Inch"
                className="w-full rounded-lg border border-gray-200 px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#004276]">
                in
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function UnitInput({ label, name, unit, value, onChange, status, disabled }) {
  const [isFocused, setIsFocused] = useState(false);

  const borderColor = {
    normal: "border-green-400 ring-green-200",
    abnormal: "border-yellow-400 ring-yellow-200",
    critical: "border-red-500 ring-red-400",
    invalid: "border-gray-200",
    empty: "border-gray-200",
  };

  const statusText = {
    normal: "Normal",
    abnormal: "Abnormal",
    critical: "Critical",
    invalid: "Invalid",
    empty: "",
  };

  const statusColor = {
    normal: "text-green-600",
    abnormal: "text-yellow-600",
    critical: "text-red-600",
    invalid: "text-gray-500",
    empty: "text-gray-400",
  };

  return (
    <div className="flex flex-col">
      {/* Label row with right-aligned status */}
      <div className="flex items-center justify-between mb-1">
        <label className="text-sm text-[#004276] font-semibold">{label}</label>
        {status !== "empty" && (
          <span className={`text-sm font-medium ${statusColor[status]}`}>
            ({statusText[status]})
          </span>
        )}
      </div>

      <div
        className={`flex items-center overflow-hidden border rounded-lg bg-white shadow-sm transition ${
          isFocused
            ? `ring-2 ${borderColor[status] || "ring-[#3cba84]"}`
            : borderColor[status] || "border-gray-200"
        }`}
      >
        <input
          type="text"
          name={name}
          value={value}
          onChange={onChange}
          disabled={disabled}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          className="flex-1 px-4 py-2 text-sm text-gray-800 outline-none"
        />
        <span className="px-3 py-2 text-sm text-[#004276] border-l border-gray-200">
          {unit}
        </span>
      </div>
    </div>
  );
}

export default function VitalDetails({patientCode,onCancel,onComplete,smartvital}) {
  const navigate = useNavigate();
  const location = useLocation();
  // const { patientCode } = createdPatientId;
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const predictedVitals = JSON.parse(sessionStorage.getItem("predictedVitals"));
console.log(patientCode,"dfghjkdfgyhuji")
  const [vitals, setVitals] = useState(() => {
    const defaultVitals = {
      height: { unit: "cm", cm: "", ft: "", inch: "" },
      weight: "",
      temperature: "",
      respirationRate: "",
      bloodPressure: "",
      pulse: "",
      heartRate: "",
      bloodGlucose: "",
      oxygenLevel: "",
      bmi: "",
      consent: true,
    };

    // If predicted vitals exist, use them
    if (predictedVitals) {
      return {
        ...defaultVitals,
        height: { ...defaultVitals.height, cm: predictedVitals.height || "" },
        weight: predictedVitals.weight || "",
        heartRate: predictedVitals.predicted_heart_rate || "",
        respirationRate: predictedVitals.breathing_rate || "",
        bloodPressure: predictedVitals.blood_pressure || "",
        oxygenLevel: predictedVitals.oxygen_level || "",
        bmi: predictedVitals.bmi_score?.toString() || "",
      };
    }

    return defaultVitals;
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Input validation for numeric-only fields
    if (["weight", "respirationRate", "oxygenLevel", "heartRate", "temperature","bloodGlucose"].includes(name)) {
      const filteredValue = value.replace(/[^0-9.]/g, "");
      setVitals((prev) => ({
        ...prev,
        [name]: filteredValue,
      }));
      return;
    }
    // Blood Pressure: allow only numbers and '/'
    // if (name === "bloodPressure") {
    //   const filteredValue = value.replace(/[^0-9/]/g, "");
    //   setVitals((prev) => ({
    //     ...prev,
    //     [name]: filteredValue,
    //   }));
    //   return;
    // }
    if (name === "bloodPressure") {
    let filteredValue = value.replace(/[^0-9/]/g, "");

    // Split the values by slash
    const [systolic, diastolic] = filteredValue.split("/");

    // Enforce max lengths
    const limitedSystolic = systolic ? systolic.slice(0, 3) : "";
    const limitedDiastolic = diastolic ? diastolic.slice(0, 2) : "";

    // Rebuild the value
    const finalValue = diastolic !== undefined
      ? `${limitedSystolic}/${limitedDiastolic}`
      : limitedSystolic;

    setVitals((prev) => ({
      ...prev,
      [name]: finalValue,
    }));
    return;
  }
    setVitals((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleHeightChange = (h) => {
    setVitals((prev) => ({ ...prev, height: h }));
  };

  const systolic = parseFloat((vitals.bloodPressure || "").split("/")[0]) || "";
  const diastolic =
    parseFloat((vitals.bloodPressure || "").split("/")[1]) || "";

  const statuses = {
    temperature: getVitalStatus(
      vitals.temperature,
      VITAL_THRESHOLDS.temperature
    ),
    pulse: getVitalStatus(vitals.pulse, VITAL_THRESHOLDS.pulse),
    heartRate: getVitalStatus(vitals.heartRate, VITAL_THRESHOLDS.heartRate),
    respirationRate: getVitalStatus(
      vitals.respirationRate,
      VITAL_THRESHOLDS.respirationRate
    ),
    bloodGlucose: getVitalStatus(
      vitals.bloodGlucose,
      VITAL_THRESHOLDS.bloodGlucose
    ),
    bloodPressureSystolic: getVitalStatus(
      systolic,
      VITAL_THRESHOLDS.bloodPressureSystolic
    ),
    bloodPressureDiastolic: getVitalStatus(
      diastolic,
      VITAL_THRESHOLDS.bloodPressureDiastolic
    ),
    weight: getVitalStatus(vitals.weight, VITAL_THRESHOLDS.weight),
    oxygenLevel: getVitalStatus(
      vitals.oxygenLevel,
      VITAL_THRESHOLDS.oxygenLevel
    ),
  };

  const heightCmValue =
    vitals.height.unit === "cm"
      ? Number(vitals.height.cm) || 0
      : (Number(vitals.height.ft) * 12 + Number(vitals.height.inch)) * 2.54 ||
        0;
  const weightKg = Number(vitals.weight) || 0;

  const computedBmi =
    heightCmValue > 0 && weightKg > 0
      ? (weightKg / (heightCmValue / 100) ** 2).toFixed(1)
      : "";

  const bmiValue = vitals.bmi !== "" ? vitals.bmi : computedBmi;

  const handleNext = async () => {
    if (!vitals.consent) {
      toast.error("Please provide consent to proceed.");
      return;
    }

    // Check if all vital fields are empty (skipped)
    const vitalFields = [
      vitals.weight,
      vitals.temperature,
      vitals.respirationRate,
      vitals.bloodPressure,
      vitals.heartRate,
      vitals.bloodGlucose,
      vitals.oxygenLevel,
      vitals.height.cm,
      vitals.height.ft,
      vitals.height.inch,
    ];
    const allVitalsEmpty = vitalFields.every((v) => !v || v === "");
    // if (allVitalsEmpty) {
    //   toast.info("Vitals Skipped");
    //   // Optionally, you can still navigate to the next page or handle as needed
    //   setIsLoggingIn(false);
    //   sessionStorage.removeItem("predictedVitals");
    //   // navigate(APP_ROUTES.MEDICALHISTORY, {
    //   //   state: {
    //   //     patientCode: patientCode,
    //   //   },
    //   // });
    //   return;
    // }

    const hasCritical = Object.values(statuses).includes("critical");

    setIsLoggingIn(true);
    const payload = {
      patientCode,
      temperature: Number(vitals.temperature) || 0,
      respirationRate: Number(vitals.respirationRate) || 0,
      bloodPressureSystolic: systolic || 0,
      bloodPressureDiastolic: diastolic || 0,
      pulse: Number(vitals.pulse) || 0,
      heartRate: Number(vitals.heartRate) || 0,
      heightFeet:
        vitals.height.unit === "ft" ? Number(vitals.height.ft) || 0 : 0,
      heightInches:
        vitals.height.unit === "ft" ? Number(vitals.height.inch) || 0 : 0,
      // Persist height in cm regardless of entry unit so downstream views work
      heightCm: Math.round(heightCmValue) || 0,
      weightInKg: Number(vitals.weight) || 0,
      bloodGlucose: Number(vitals.bloodGlucose) || 0,
      oxygenLevel: Number(vitals.oxygenLevel) || 0,
      bmi: bmiValue ? Number(bmiValue) : 0,
    };

    const token = localStorage.getItem("auth_token");
    const tenantId = localStorage.getItem("tenantId");
    console.log(payload,"payload")
    try {
      const res = await fetch(`${API_URL}patient-vitals/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          tenantId: tenantId,
        },
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      sessionStorage.removeItem("predictedVitals");
      localStorage.setItem("vitaldata", JSON.stringify(result.data));
      setIsLoggingIn(false);
      if (res.ok) {
        toast.success("Vitals Saved Successfully!");
        sessionStorage.removeItem("predictedVitals");
        if (onComplete) {
          onComplete(result.data);
        }
        onCancel();
        // navigate(APP_ROUTES.MEDICALHISTORY, {
        //   state: {
        //     patientCode: patientCode,
        //   },
        // });
      } else {
        toast.error(result.message || result.error || "Failed to save vitals");
      }
    } catch (err) {
      console.error(err);
      toast.error("Network error saving vitals, please try again");
    }
  };
console.log(patientCode,"patientCode")
  return (
    <>
      {isLoggingIn && <FullScreenLoader message="Saving Vital Info..." />}
      <>
        <div className="min-h-screen px-2 sm:px-4 md:pt-10 bg-white">
          {/* {smartvital &&(
          <div className="bg-[#ECFEFF] border border-[#B0E7F9] rounded-lg p-2 sm:p-4 mb-4 sm:mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-sm">
            <div className="text-[#004276] text-xs sm:text-sm md:text-base font-medium">
              Do you want to take the vitals through{" "}
              <span className="text-[#087A9C] font-semibold">Camera</span>?
            </div>
            <button
              onClick={() =>
                navigate(APP_ROUTES.SMART_VITAL_FORM, {
                  state: {
                    patientCode: patientCode,
                  },
                })
              }
              className="bg-gradient-to-r from-[#004276] to-[#3cba84] text-white text-xs sm:text-sm md:text-base px-4 sm:px-5 py-2 rounded-full flex items-center gap-2 shadow-md hover:opacity-90"
            >
              <CameraOutlined />
              Take Smart Vital
            </button>
          </div>
          )} */}

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            <HeightInput height={vitals.height} onChange={handleHeightChange} />
            <UnitInput
              label="Weight"
              name="weight"
              unit="kg"
              value={vitals.weight}
              onChange={handleChange}
             status="empty"
            />

            <UnitInput
              label="BMI"
              name="bmi"
              unit="kg/m²"
              value={bmiValue}
              onChange={() => {}}
              status="empty"
              disabled
            />

            <UnitInput
              label="Respiration Rate"
              name="respirationRate"
              unit="bpm"
              value={vitals.respirationRate}
              onChange={handleChange}
              status={statuses.respirationRate}
            />
            <UnitInput
              label="Oxygen Level (SpO₂)"
              name="oxygenLevel"
              unit="%"
              value={vitals.oxygenLevel}
              onChange={handleChange}
              status={statuses.oxygenLevel}
            />
            <UnitInput
              label="Heart Rate"
              name="heartRate"
              unit="Beat/min"
              value={vitals.heartRate}
              onChange={handleChange}
              status={statuses.heartRate}
            />
            <UnitInput
              label="Blood Pressure"
              name="bloodPressure"
              unit="mmHg"
              value={vitals.bloodPressure}
              onChange={handleChange}
              status={
                statuses.bloodPressureSystolic === "critical" ||
                statuses.bloodPressureDiastolic === "critical"
                  ? "critical"
                  : statuses.bloodPressureSystolic === "abnormal" ||
                    statuses.bloodPressureDiastolic === "abnormal"
                  ? "abnormal"
                  : statuses.bloodPressureSystolic === "normal" &&
                    statuses.bloodPressureDiastolic === "normal"
                  ? "normal"
                  : "empty"
              }
            />
            {/* <UnitInput
              label="Pulse"
              name="pulse"
              unit="bpm"
              value={vitals.pulse}
              onChange={handleChange}
              status={statuses.pulse}
            /> */}
            <UnitInput
              label="Temperature"
              name="temperature"
              unit="°F"
              value={vitals.temperature}
              onChange={handleChange}
              status={statuses.temperature}
            />

            <UnitInput
              label="Blood Glucose"
              name="bloodGlucose"
              unit="mg/dL"
              value={vitals.bloodGlucose}
              onChange={handleChange}
              status={statuses.bloodGlucose}
            />
          </div>

          <div className="mt-4 sm:mt-6">
            <label className="flex items-start gap-2 text-xs sm:text-sm text-[#004172]">
              <input
                type="checkbox"
                name="consent"
                checked={vitals.consent}
                onChange={handleChange}
                className="mt-1"
              />
              I consent to share my health information with my doctor for the
              purpose of medical care and treatment.
            </label>
          </div>

          <div className="flex flex-col items-center mt-8 md:mt-10">
            <button
              onClick={handleNext}
              disabled={isLoggingIn}
              className="bg-gradient-to-r w-full sm:w-[335px] from-[#004172] to-[#3cba84] text-white rounded-full px-10 py-2 font-medium"
            >
              {isLoggingIn ? "Loading..." : "Next"}
            </button>
          </div>
        </div>
      </>
    </>
  );
}
